// Payment Service for BookAPT
// Handles Stripe payment processing and subscription management

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account' | 'paypal';
  last4: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  stripePriceId: string;
}

export interface PaymentResult {
  success: boolean;
  subscriptionId?: string;
  error?: string;
  clientSecret?: string;
}

export class PaymentService {
  private static readonly API_BASE_URL = 'https://your-backend-api.com/api'; // Replace with your backend URL
  private static readonly STRIPE_PUBLISHABLE_KEY = 'pk_test_your_stripe_publishable_key'; // Replace with your Stripe key

  // Available subscription plans for business owners
  static readonly BUSINESS_PLANS: SubscriptionPlan[] = [
    {
      id: 'basic',
      name: 'Basic',
      price: 29,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 50 appointments/month',
        'Basic scheduling',
        'Customer management',
        'Email support'
      ],
      stripePriceId: 'price_basic_monthly' // Replace with actual Stripe price ID
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 59,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 200 appointments/month',
        'Advanced scheduling',
        'Worker management',
        'Basic analytics',
        'Priority support'
      ],
      stripePriceId: 'price_professional_monthly' // Replace with actual Stripe price ID
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Unlimited appointments',
        'All Professional features',
        'Advanced analytics & reporting',
        'Custom integrations',
        '24/7 phone support'
      ],
      stripePriceId: 'price_enterprise_monthly' // Replace with actual Stripe price ID
    },
    // Founding Member Plans - 50% off Professional plan
    {
      id: 'founding-member-annual',
      name: 'Professional - Founding Member (Annual)',
      price: 179.94,
      currency: 'usd',
      interval: 'year',
      features: [
        'Everything in Professional plan',
        '50% off first year',
        'Locked-in price for 5 years',
        'Priority support',
        'Founding Member badge',
        'Up to 200 appointments/month',
        'Advanced scheduling',
        'Worker management',
        'Basic analytics',
        'Priority support'
      ],
      stripePriceId: 'price_founding_member_annual' // ⚠️ REPLACE with actual Stripe Price ID from Dashboard
    },
    {
      id: 'founding-member-monthly',
      name: 'Professional - Founding Member (Monthly)',
      price: 14.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Everything in Professional plan',
        '50% off first year',
        'Locked-in price for 5 years',
        'Priority support',
        'Founding Member badge',
        'Up to 200 appointments/month',
        'Advanced scheduling',
        'Worker management',
        'Basic analytics',
        'Priority support'
      ],
      stripePriceId: 'price_founding_member_monthly' // ⚠️ REPLACE with actual Stripe Price ID from Dashboard
    }
  ];

  // Available subscription plans for regular users
  static readonly USER_PLANS: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 5 appointments/month',
        'Basic booking',
        'Email notifications',
        'Basic support',
        'Mobile app access',
      ],
      stripePriceId: 'price_free',
    },
    {
      id: 'trial',
      name: '30-Day Free Trial',
      price: 0,
      currency: 'usd',
      interval: 'month',
      features: [
        '30 days of Premium features',
        'Up to 50 appointments/month',
        'Advanced booking features',
        'SMS & Email notifications',
        'Priority support',
        'Calendar sync',
        'Appointment reminders',
        'Basic analytics',
        'Auto-converts to Premium after trial',
      ],
      stripePriceId: 'price_trial',
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 9.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 50 appointments/month',
        'Advanced booking features',
        'SMS & Email notifications',
        'Priority support',
        'Calendar sync',
        'Appointment reminders',
        'Basic analytics',
      ],
      stripePriceId: 'price_premium_monthly',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Up to 100 appointments/month',
        'All Premium features',
        'Advanced analytics',
        'Custom booking forms',
        'API access',
        '24/7 support',
        'Multi-service booking',
        'Payment processing',
      ],
      stripePriceId: 'price_pro_monthly',
    },
    {
      id: 'business',
      name: 'Business',
      price: 39.99,
      currency: 'usd',
      interval: 'month',
      features: [
        'Unlimited appointments',
        'All Pro features',
        'Team management',
        'Advanced reporting',
        'White-label options',
        'Custom integrations',
        'Dedicated support',
        'Priority feature requests',
      ],
      stripePriceId: 'price_business_monthly',
    },
  ];

  // Legacy property for backward compatibility
  static readonly PLANS = this.BUSINESS_PLANS;

  // Create a payment intent for subscription
  static async createPaymentIntent(
    planId: string,
    businessOwnerId: string,
    paymentMethodId?: string,
    walletType?: 'apple_pay' | 'google_pay'
  ): Promise<PaymentResult> {
    try {
      const plan = this.PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      const response = await fetch(`${this.API_BASE_URL}/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify({
          planId: plan.id,
          businessOwnerId: businessOwnerId,
          paymentMethodId: paymentMethodId,
          priceId: plan.stripePriceId,
          walletType: walletType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment intent');
      }

      return {
        success: true,
        subscriptionId: data.subscriptionId,
        clientSecret: data.clientSecret
      };
    } catch (error) {
      console.error('Error creating payment intent:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Confirm payment with Stripe
  static async confirmPayment(
    clientSecret: string,
    paymentMethodId: string
  ): Promise<PaymentResult> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify({
          clientSecret,
          paymentMethodId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Payment confirmation failed');
      }

      return {
        success: true,
        subscriptionId: data.subscriptionId
      };
    } catch (error) {
      console.error('Error confirming payment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment confirmation failed'
      };
    }
  }

  // Get customer's payment methods
  static async getPaymentMethods(customerId: string): Promise<PaymentMethod[]> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/payment-methods/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch payment methods');
      }

      return data.paymentMethods || [];
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      return [];
    }
  }

  // Add a new payment method
  static async addPaymentMethod(
    customerId: string,
    paymentMethodId: string
  ): Promise<PaymentResult> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/add-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify({
          customerId,
          paymentMethodId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add payment method');
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error adding payment method:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add payment method'
      };
    }
  }

  // Cancel subscription
  static async cancelSubscription(subscriptionId: string): Promise<PaymentResult> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify({
          subscriptionId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to cancel subscription'
      };
    }
  }

  // Get subscription status
  static async getSubscriptionStatus(customerId: string): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/subscription-status/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${await this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription status');
      }

      return data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      return null;
    }
  }

  // Get auth token (implement based on your auth system)
  private static async getAuthToken(): Promise<string> {
    // This should return the user's authentication token
    // Implement based on your auth system
    return 'your-auth-token';
  }

  // Get plan by ID
  static getPlanById(planId: string): SubscriptionPlan | undefined {
    return this.PLANS.find(plan => plan.id === planId);
  }

  // Get all plans
  static getAllPlans(): SubscriptionPlan[] {
    return this.PLANS;
  }

  // Get user-specific plans
  static getUserPlans(): SubscriptionPlan[] {
    return this.USER_PLANS;
  }

  // Get business owner plans
  static getBusinessPlans(): SubscriptionPlan[] {
    return this.BUSINESS_PLANS;
  }

  // Get plans by user type
  static getPlansByUserType(userType: 'customer' | 'business'): SubscriptionPlan[] {
    return userType === 'customer' ? this.USER_PLANS : this.BUSINESS_PLANS;
  }
}

export default PaymentService;
